import { useMemo } from "react";
import { CloseTemplateViewConfig } from "@common/types/closeTemplateViewConfig";
import { TaskType } from "@common/types/task";
import { getLastCompletedMonthDates } from "../closePeriodSelector.utils";
import { ALL_PREVIEW_ENTITIES, PreviewEntityScope } from "./previewEntityScope";
import { ResolvedTaskView, useResolvedTaskView } from "./useResolvedTaskView";
import {
    TemplatePreviewTarget,
    useTemplatePreviewTarget,
} from "./useTemplatePreviewTarget";

interface UseTemplateTaskPreviewParams {
    taskType: TaskType;
    viewConfig: CloseTemplateViewConfig | null;
    entityScope?: PreviewEntityScope;
}

interface TemplateTaskPreview {
    previewTarget: TemplatePreviewTarget;
    resolvedView: ResolvedTaskView;
}

/**
 * The previewed client and entity, and the task view resolved for them over
 * the last completed month.
 */
export const useTemplateTaskPreview = ({
    taskType,
    viewConfig,
    entityScope = ALL_PREVIEW_ENTITIES,
}: UseTemplateTaskPreviewParams): TemplateTaskPreview => {
    const previewTarget = useTemplatePreviewTarget({ entityScope });
    const previewPeriod = useMemo(() => getLastCompletedMonthDates(), []);
    const resolvedView = useResolvedTaskView({
        taskType,
        viewConfig,
        entityId: previewTarget.entityId,
        isEntityLoading: previewTarget.isLoading,
        noEntityReason: entityScope.noEntityReason,
        period: previewPeriod,
    });

    return { previewTarget, resolvedView };
};
